import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { formatMoney } from "@/lib/formatMoney";
import { DEFAULT_CURRENCY } from "@/lib/currencies";
import { getStoredToken } from "@/lib/apiClient";
import { clearPendingOnboarding, markPendingOnboarding } from "@/lib/onboardingFlags";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountantContext = createContext({
  name: "Kola",
  settings: null,
  loading: true,
  settingsError: false,
  refresh: () => {},
  updateSettings: async () => {},
  onboardingComplete: false,
  markOnboardingComplete: async () => {},
});

export function AccountantProvider({ children }) {
  const { token, loading: authLoading, logout } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsError, setSettingsError] = useState(false);

  const refresh = useCallback(async () => {
    if (!getStoredToken()) {
      setSettings(null);
      setSettingsError(false);
      setLoading(false);
      return null;
    }
    setLoading(true);
    setSettingsError(false);
    try {
      const r = await axios.get(`${API}/settings`);
      setSettings(r.data);
      if (r.data?.onboarding_complete === false) {
        markPendingOnboarding();
      } else if (r.data?.onboarding_complete === true) {
        clearPendingOnboarding();
      }
      return r.data;
    } catch (e) {
      console.error("Failed to load settings", e);
      setSettings(null);
      if (e.response?.status === 401) {
        logout();
      } else {
        setSettingsError(true);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [logout]);

  // Wait for auth to finish before loading settings (avoids stale-token races)
  useEffect(() => {
    if (authLoading) return;
    if (!token) {
      setSettings(null);
      setSettingsError(false);
      setLoading(false);
      return;
    }
    setSettings(null);
    setLoading(true);
    refresh();
  }, [token, authLoading, refresh]);

  const updateSettings = useCallback(async (patch) => {
    const r = await axios.put(`${API}/settings`, patch);
    setSettings(r.data);
    setSettingsError(false);
    return r.data;
  }, []);

  const markOnboardingComplete = useCallback(async () => {
    return updateSettings({ onboarding_complete: true });
  }, [updateSettings]);

  const currency = settings?.currency || DEFAULT_CURRENCY;
  const fmt = useMemo(() => (n) => formatMoney(n, currency), [currency]);

  const value = {
    name: settings?.accountant_name || "Kola",
    settings,
    currency,
    fmt,
    loading,
    settingsError,
    refresh,
    updateSettings,
    onboardingComplete: !!settings?.onboarding_complete,
    markOnboardingComplete,
  };

  return (
    <AccountantContext.Provider value={value}>
      {children}
    </AccountantContext.Provider>
  );
}

export function useAccountant() {
  return useContext(AccountantContext);
}
